import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { QrCode2, Science as ScienceIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';
import { experimentColors } from '../../../themes/experimentColors';
import QrCodeModal from '../../../components/QrCodeModal/QrCodeModal';
import EnhancedSlot from './EnhancedSlot';
import HeatmapLegend from './HeatmapLegend';
import useHeatmapColors from '../hooks/useHeatmapColors';
import useCategoryGroupBorders from '../hooks/useCategoryGroupBorders';

const EnhancedPlanogram = ({
  machineData,
  selectedSlots,
  onSlotInspect,
  onSlotToggleSelect,
  isActMode = false,
  onMachineClick,
  heatmapMode: externalHeatmapMode,
  onHeatmapModeChange,
  compact = false,
  period = 14,
  categoryOverlay = false
}) => {
  const { t } = useTranslation();
  const [internalHeatmapMode, setInternalHeatmapMode] = React.useState('none');
  const [qrModalOpen, setQrModalOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = React.useState(360);

  // Use external heatmap state if provided (from top bar), otherwise internal
  const heatmapMode = externalHeatmapMode !== undefined ? externalHeatmapMode : internalHeatmapMode;
  const setHeatmapMode = onHeatmapModeChange || setInternalHeatmapMode;

  const resolvedMachineData = machineData ?? { rows: 0, columns: 0, slots: [], machineId: null };
  const { rows, columns, slots, machineId } = resolvedMachineData;
  const hasMachineData = Boolean(machineData);

  const { colorMap: heatmapColors, valueMap: heatmapValueMap, absoluteValueMap, categoryMap, minVal, maxVal } = useHeatmapColors(slots, heatmapMode);

  // Measure container width for responsive cell sizing
  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width > 0) setContainerWidth(width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Container-aware grid configuration: fit cells to the available width while they
  // stay readable; once a fit would shrink them below a usable minimum (big planograms
  // on small screens), lock them to that minimum and scroll horizontally instead — so
  // every cell stays visible and reachable rather than being squished/clipped.
  const gridConfig = React.useMemo(() => {
    const safeColumns = columns > 0 ? columns : 1;
    const gridPadding = 24; // 12px each side
    const gap = 6;
    const minCellWidth = 84; // below this the slot content can't be read → scroll instead
    const maxCellWidth = 200;

    const availableWidth = containerWidth - gridPadding;
    const naturalCellWidth = (availableWidth - (safeColumns - 1) * gap) / safeColumns;

    // Fits comfortably → fill the width. Too tight → fixed readable width + horizontal scroll.
    const needsScroll = naturalCellWidth < minCellWidth;
    const cellWidth = needsScroll
      ? minCellWidth
      : Math.min(maxCellWidth, Math.floor(naturalCellWidth));

    // Height proportional to width (0.72 ratio), clamped
    const cellHeight = Math.min(140, Math.max(72, Math.round(cellWidth * 0.72)));

    // Fonts scale with cell size so name + metrics stay readable and never overflow
    // the cell — metrics sit one step below the name and never go under 9px.
    const nameFont = cellWidth >= 160 ? 14 : cellWidth >= 130 ? 13 : cellWidth >= 110 ? 12 : cellWidth >= 90 ? 11 : 10;
    const metricFont = Math.max(9, nameFont - 1);

    // Tighten gap for narrow cells to reclaim space
    const effectiveGap = cellWidth < 90 ? 4 : gap;
    const totalGridWidth = safeColumns * cellWidth + (safeColumns - 1) * effectiveGap;

    return {
      cellWidth,
      cellHeight,
      fontSize: `${nameFont}px`,
      metricFontSize: `${metricFont}px`,
      gap: `${effectiveGap}px`,
      padding: '12px',
      needsScroll,
      totalGridWidth
    };
  }, [columns, containerWidth]);

  // Pre-compute grid layout with double-width support
  const gridItems = React.useMemo(() => {
    if (!hasMachineData) return [];

    const items = [];
    const slotMap = new Map();
    slots.forEach((slot) => {
      const key = `${slot.row}-${slot.column}`;
      slotMap.set(key, slot);
    });

    for (let rowIndex = 1; rowIndex <= rows; rowIndex++) {
      let colIndex = 1;

      while (colIndex <= columns) {
        const key = `${rowIndex}-${colIndex}`;
        const slot = slotMap.get(key);

        if (slot) {
          const isDouble = slot.laneType === 'double';

          if (isDouble && items.length > 0) {
            const previousItem = items[items.length - 1];
            if (
              previousItem &&
              previousItem.kind === 'empty' &&
              previousItem.row === rowIndex &&
              previousItem.columnStart === colIndex - 1
            ) {
              items[items.length - 1] = {
                kind: 'slot',
                slot,
                row: rowIndex,
                columnStart: previousItem.columnStart,
                columnSpan: 2
              };
              colIndex += 1;
              continue;
            }
          }

          if (isDouble) {
            const nextKey = `${rowIndex}-${colIndex + 1}`;
            const nextSlot = slotMap.get(nextKey);
            const canSpanRight = !nextSlot && colIndex < columns;

            if (canSpanRight) {
              items.push({
                kind: 'slot',
                slot,
                row: rowIndex,
                columnStart: colIndex,
                columnSpan: 2
              });
              colIndex += 2;
              continue;
            }
          }

          items.push({
            kind: 'slot',
            slot,
            row: rowIndex,
            columnStart: colIndex,
            columnSpan: 1
          });
          colIndex += 1;
          continue;
        }

        items.push({
          kind: 'empty',
          row: rowIndex,
          columnStart: colIndex,
          columnSpan: 1
        });
        colIndex += 1;
      }
    }

    return items;
  }, [hasMachineData, slots, rows, columns]);

  // Compute category group borders (Lego-block effect)
  const { borders: categoryBorders, legend: categoryBorderLegend } = useCategoryGroupBorders(
    slots, gridItems, rows, columns, categoryOverlay
  );

  if (!hasMachineData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box data-machine-id={machineId}>
      {/* Show title/QR/heatmap controls only when NOT in compact (top-bar) mode */}
      {!compact && (
        <>
          {/* Machine Title */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
              <Box
                onClick={() => onMachineClick?.(machineData)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  px: '16px',
                  py: '8px',
                  borderRadius: '8px',
                  backgroundColor: colors.action.primary,
                  color: 'white',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: colors.action.primaryHover || '#1976d2',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <Typography sx={{
                  fontSize: '16px',
                  color: 'white',
                  fontWeight: 600
                }}>
                  {machineData.location}
                </Typography>
              </Box>

              {machineData.qrCode && (
                <IconButton
                  onClick={() => setQrModalOpen(true)}
                  sx={{
                    color: colors.text.primary,
                    backgroundColor: colors.background.subtle,
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: colors.background.surface,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                  title={t('viewQrCode')}
                >
                  <QrCode2 sx={{ fontSize: '24px' }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Heatmap controls */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
            px: 1
          }}>
            <HeatmapLegend mode={heatmapMode} onModeChange={setHeatmapMode} minVal={minVal} maxVal={maxVal} categoryLegend={categoryMap?._legend} period={period} />
          </Box>
        </>
      )}

      {/* Legend */}
      <Box sx={{
        mb: '8px',
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Category color legend (when overlay is active) */}
        {categoryOverlay && categoryBorderLegend.length > 0 && (
          <>
            {categoryBorderLegend.map(item => (
              <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '3px',
                  border: `2.5px solid ${item.color}`,
                  backgroundColor: 'transparent',
                  flexShrink: 0
                }} />
                <Typography sx={{ fontSize: '11px', color: colors.text.secondary, whiteSpace: 'nowrap' }}>
                  {item.name}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {/* Category color legend (when in category heatmap mode) */}
        {!categoryOverlay && heatmapMode === 'category' && categoryMap?._legend && (
          <>
            <HeatmapLegend mode={heatmapMode} onModeChange={() => {}} minVal={minVal} maxVal={maxVal} showToggle={false} categoryLegend={categoryMap._legend} period={period} />
          </>
        )}
      </Box>

      {/* Planogram Grid */}
      <Box
        ref={containerRef}
        sx={{
          backgroundColor: colors.background.surface,
          borderRadius: '12px',
          boxShadow: `0 1px 3px ${colors.shadow.subtle}`,
          p: gridConfig.padding,
          width: '100%',
          overflow: gridConfig.needsScroll ? 'auto' : 'clip',
          // When scrolling is needed, keep the horizontal scrollbar visible so it's
          // obvious the planogram can be swiped left/right to reach off-screen cells.
          ...(gridConfig.needsScroll && {
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0,0,0,0.35) rgba(0,0,0,0.08)',
            '&::-webkit-scrollbar': { height: '10px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '5px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: '5px' },
          })
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridConfig.needsScroll
              ? `repeat(${columns}, ${gridConfig.cellWidth}px)`
              : `repeat(${columns}, 1fr)`,
            gridAutoRows: `${gridConfig.cellHeight}px`,
            gap: gridConfig.gap,
            justifyContent: gridConfig.needsScroll ? 'start' : 'center',
            alignContent: 'start',
            backgroundColor: colors.background.subtle,
            borderRadius: '8px',
            padding: '12px',
            ...(gridConfig.needsScroll && {
              width: 'max-content',
              minWidth: `${gridConfig.totalGridWidth}px`
            })
          }}
        >
          {gridItems.map((item, index) => {
            const slot = item.kind === 'slot' ? item.slot : null;
            const gridKey = slot
              ? `slot-${slot.row}-${slot.column}-${slot.selection_id ?? index}`
              : `empty-${item.row}-${item.columnStart}-${index}`;
            const slotKey = slot ? `${slot.row}-${slot.column}` : null;
            const isSelected = slotKey && selectedSlots ? selectedSlots.has(slotKey) : false;

            const cb = categoryOverlay ? categoryBorders.get(`${item.row}-${item.columnStart}`) : null;

            return (
              <Box
                key={gridKey}
                sx={{
                  gridColumn: `${item.columnStart} / span ${item.columnSpan}`,
                  gridRow: `${item.row}`,
                  // Category group borders (Lego-block effect)
                  ...(cb && {
                    borderTop: cb.top ? `${cb.borderWidth} solid ${cb.color}` : 'none',
                    borderRight: cb.right ? `${cb.borderWidth} solid ${cb.color}` : 'none',
                    borderBottom: cb.bottom ? `${cb.borderWidth} solid ${cb.color}` : 'none',
                    borderLeft: cb.left ? `${cb.borderWidth} solid ${cb.color}` : 'none',
                    borderRadius: cb.borderRadius,
                    backgroundColor: cb.bgColor,
                  })
                }}
              >
                <EnhancedSlot
                  slot={slot}
                  heatmapBg={slotKey ? heatmapColors[slotKey] : undefined}
                  isSelected={isSelected}
                  onInspect={onSlotInspect}
                  onToggleSelect={onSlotToggleSelect}
                  isActMode={isActMode}
                  cellSize={gridConfig.cellHeight}
                  fontSize={gridConfig.fontSize}
                  metricFontSize={gridConfig.metricFontSize}
                  heatmapMode={heatmapMode}
                  heatmapValue={slotKey ? heatmapValueMap[slotKey] : undefined}
                  absoluteValue={slotKey ? absoluteValueMap[slotKey] : undefined}
                  period={period}
                  categoryOverlay={categoryOverlay}
                />
              </Box>
            );
          })}
        </Box>

        {/* Period context */}
        {compact && (
          <Typography sx={{
            fontSize: '10px',
            color: colors.text.muted,
            textAlign: 'center',
            mt: '6px',
            fontStyle: 'italic'
          }}>
            {t('machines_periodContext', { days: period })}
          </Typography>
        )}
      </Box>

      {/* QR Code Modal */}
      <QrCodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        qrData={machineData.qrCode}
        machineId={machineData.location}
      />
    </Box>
  );
};

export default React.memo(EnhancedPlanogram);
